/**
 * @file Drawer 右侧抽屉
 * @description 侧滑面板；fade + 位移；Esc / 遮罩关闭。
 *
 * @whenToUse 详情编辑、筛选扩展、CRUD 抽屉流。
 * @whenNotToUse 居中确认（Dialog）。
 */

import { useEffect, useId, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'

export type DrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  children: ReactNode
  footer?: ReactNode
  /** 宽度 class，默认 max-w-md */
  className?: string
}

export function Drawer({
  open,
  onOpenChange,
  title,
  children,
  footer,
  className,
}: DrawerProps) {
  const titleId = useId()
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(open)

  useEffect(() => {
    if (open) {
      setMounted(true)
      const id = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(id)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false)
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onOpenChange])

  if (!mounted || typeof document === 'undefined') return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="关闭抽屉"
        className={cn(
          'absolute inset-0 bg-foreground/40 transition-opacity duration-200',
          visible ? 'opacity-100' : 'opacity-0',
        )}
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'relative z-10 flex h-full w-full max-w-md flex-col border-l border-border bg-raised transition-transform duration-200',
          visible ? 'translate-x-0' : 'translate-x-full',
          className,
        )}
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
          <h2 id={titleId} className="text-base font-medium tracking-tight">
            {title}
          </h2>
          <button
            type="button"
            aria-label="关闭"
            className="inline-flex size-8 items-center justify-center rounded-lg border border-border text-muted transition-opacity duration-200 hover:opacity-90"
            onClick={() => onOpenChange(false)}
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <ScrollArea className="min-h-0 flex-1 px-4 py-2">{children}</ScrollArea>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-2">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
